/**
 * Der Kurs, auf dem eine Zeichnung an einem bestimmten Handelstag liegt.
 *
 * Gebraucht wird das, um zwei Indikatoren miteinander zu vergleichen: Eine
 * waagerechte Linie hat überall denselben Kurs, eine Trendlinie oder ein Tunnel
 * an jedem Tag einen anderen.
 *
 * **Gerechnet wird in Handelstagen, nicht in Kalendertagen.** Der Chart setzt
 * seine Zeichnungen über logische Indizes — der Abstand zwischen zwei Kerzen ist
 * dort immer eins, gleich ob ein Wochenende dazwischenliegt. Eine Interpolation
 * über Kalendertage ergäbe an jedem Montag einen anderen Kurs als den gezeichneten,
 * und der Unterschied wüchse mit jedem Feiertag. Deshalb die Liste der Handelstage
 * als Parameter: Sie ist die gemeinsame Zeitachse von Chart und Rechnung.
 *
 * In `shared`, weil beide Seiten es brauchen — der Server für die tägliche
 * Auswertung, die Oberfläche für die Anzeige des Abstands.
 */
/** Sucht den Platz eines Datums in der Handelstagsreihe. */
const indexOf = (date, tradingDays) => {
    const index = tradingDays.indexOf(date);
    return index === -1 ? null : index;
};
/**
 * Linearer Verlauf zwischen zwei Ankern, über die Indizes der Handelstage.
 *
 * Außerhalb der Anker wird fortgeschrieben, nicht abgeschnitten: Eine Trendlinie
 * wird gerade deshalb gezogen, weil sie über ihren letzten Anker hinaus etwas
 * aussagt. Fielen beide Anker auf denselben Tag, gäbe es keine Steigung — dann
 * gilt der Kurs des Anfangs.
 */
const alongLine = (from, to, date, tradingDays) => {
    const [i, j, k] = [
        indexOf(from.date, tradingDays),
        indexOf(to.date, tradingDays),
        indexOf(date, tradingDays),
    ];
    if (i === null || j === null || k === null)
        return null;
    if (i === j)
        return from.price;
    return from.price + ((to.price - from.price) * (k - i)) / (j - i);
};
/**
 * Der Kurs einer Zeichnung am angegebenen Handelstag.
 *
 * `null`, wenn er sich nicht bestimmen lässt — etwa weil ein Anker auf einem Tag
 * liegt, für den keine Kerze vorliegt. Dann ist keine Aussage möglich, und eine
 * ausgedachte wäre schlechter als keine.
 */
export const drawingPriceAt = (payload, date, tradingDays) => {
    switch (payload.type) {
        case 'stop_loss':
        case 'horizontal_line':
            /* Waagerecht: überall derselbe Kurs, der Tag spielt keine Rolle. */
            return payload.price;
        case 'trend_line':
            return alongLine(payload.from, payload.to, date, tradingDays);
        case 'channel': {
            /*
             * Beim Tunnel zählt die **untere** Begrenzung: Sie ist die Unterstützung,
             * unter der ein Stop liegt. Die obere wäre für diesen Vergleich die falsche
             * Kante — sie sagt etwas über das Ziel, nicht über die Absicherung.
             *
             * `offset` kann positiv oder negativ sein, je nachdem, auf welcher Seite die
             * parallele Linie gezogen wurde. Deshalb das Minimum beider, nicht die
             * Annahme, die Basislinie sei die untere.
             */
            const basis = alongLine(payload.from, payload.to, date, tradingDays);
            if (basis === null)
                return null;
            return Math.min(basis, basis + payload.offset);
        }
    }
};
//# sourceMappingURL=drawing-geometry.js.map